import {Component, Input, OnInit} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {ActivatedRoute} from "@angular/router";
import {DiscountsService, TOAST_STATE, ToastService} from "@services";
import {
  Discount,
  UserDetail,
  DiscountType,
  DiscountItem,
  EventItem,
  DiscountCoversType,
  CouponUserRequest,
  Organiser
} from "@model";
import {getLanguageValue, required} from "@utility";
import {
  AbstractComponent,
  AddDiscountItemModalComponent,
  ConfirmationModalComponent, EventsListModalComponent,
  UserListModalComponent
} from "@components";

@Component({
  selector: 'app-discount-details',
  templateUrl: './discounts-details.component.html',
  styleUrls: ['./discounts-details.component.scss']
})
export class DiscountsDetailsComponent extends AbstractComponent implements OnInit {
  protected readonly DiscountType = DiscountType;
  @Input() discount: Discount | undefined;
  organiserId: number | undefined;
  currentOrganiser: Organiser | undefined;

  public isCouponSettingsCollapsed: boolean = false;
  public isUserWhoHaveUsedTheCouponCollapsed: boolean = true;
  public isUserWhoHaveNotUsedTheCouponCollapsed: boolean = true;

  userWithUsedCoupons: UserDetail[] = [];
  userWithUnusedCoupons: UserDetail[] = [];

  startDate: Date | undefined;
  endDate: Date | undefined;

  inputForm = new FormGroup({
    discountType: new FormControl(),
    discountCode: new FormControl(),
    periodValidation: new FormControl(),
    name: new FormControl(),
    description: new FormControl(),
    discountApplication: new FormControl(),
    discountOnMoreRaces: new FormControl(),
    numberOfRaces: new FormControl(),
    isCodeActive: new FormControl(),
    amountOrPercentage: new FormControl(),
    whatDiscountCovers: new FormControl(),
    singleUsageOfDiscount: new FormControl(),
    active: new FormControl(),
    usageLimit: new FormControl(),
    usageMaxAllowed: new FormControl(),
    standaloneUsageOfDiscount: new FormControl(),
    eventsRacesDiscountIsAppliedTo: new FormControl()
  });

  constructor(public translocoService: TranslocoService, private discountsService: DiscountsService,
              private toastService: ToastService, private route: ActivatedRoute, private modalService: NgbModal) {
    super();
  }

  ngOnInit(): void {

    this.organiserId = +this.route.snapshot.queryParams['organiserId'];
    this.initForm();
    if (this.discount && this.discount.id) {
      this.subscriptions.push(this.discountsService.findUsersWithCoupon(this.discount.id, true, this.organiserId).subscribe(
        (value: UserDetail[]) => {
          this.userWithUnusedCoupons = value ?? [];
        }
      ));
      this.subscriptions.push(this.discountsService.findUsersWithUsedCoupon(this.discount.id, true, this.organiserId).subscribe(
        (value: UserDetail[]) => {
          this.userWithUsedCoupons = value ?? [];
        }
      ));
    }
  }


  private initForm(): void {
    if (this.discount) {
      this.inputForm.patchValue(this.discount);
      this.inputForm.controls.discountType.addValidators([required]);
      if (this.discount.discountType === DiscountType.COUPON) {
        this.inputForm.controls.discountCode.addValidators([required]);
        this.inputForm.controls.discountOnMoreRaces.disable();
        this.inputForm.patchValue({
          discountOnMoreRaces: 2
        });
        this.inputForm.controls.discountOnMoreRaces.disable();
        this.inputForm.controls.numberOfRaces.setValue(undefined);
        this.inputForm.controls.numberOfRaces.disable();
      } else {
        this.inputForm.controls.discountCode.setValue(undefined);
        this.inputForm.controls.discountCode.disable();
        this.inputForm.controls.usageLimit.setValue(undefined);
        this.inputForm.controls.usageLimit.disable();
        this.inputForm.controls.usageMaxAllowed.setValue(undefined);
        this.inputForm.controls.usageMaxAllowed.disable();
        this.inputForm.controls.discountApplication.addValidators([required]);
        if (this.discount && this.discount.discountOnMoreRaces && this.discount.discountOnMoreRacesItems && this.discount.discountOnMoreRacesItems.length > 0) {
          let discountOnMoreRacesItems: DiscountItem[] = this.discount.discountOnMoreRacesItems;
          let tags: string[] = discountOnMoreRacesItems.map((value: DiscountItem) => {
            return this.constructDiscountItemUILabel(value);
          });
          this.inputForm.controls.numberOfRaces.setValue(tags);
        }
      }
      if (this.discount && this.discount.events && this.discount.events.length > 0) {
        let eventsRacesDiscountIsAppliedToItems: EventItem[] = this.discount.events;
        // @ts-ignore
        let tags: string[] = eventsRacesDiscountIsAppliedToItems.map((value: EventItem) => {
          return getLanguageValue(value.name, value.nameEng, value.nameIta, value.nameSlo, value.nameSr,value.nameBs, "hr") ?? ""
        });
        this.inputForm.controls.eventsRacesDiscountIsAppliedTo.setValue(tags);
      }

      this.inputForm.controls.name.addValidators([required]);
      this.inputForm.controls.description.addValidators([required]);
      this.inputForm.controls.periodValidation.addValidators([required])
      this.inputForm.controls.whatDiscountCovers.addValidators([required]);

      this.inputForm.patchValue({amountOrPercentage: this.discount.amount});
      this.inputForm.controls.periodValidation.patchValue({from: this.discount.dateFrom, to: this.discount.dateTo});
      if (this.discount.discountCovers === DiscountCoversType.REGISTRATION_FEE) {
        this.inputForm.patchValue({
          whatDiscountCovers: 1
        });
      } else if (this.discount.discountCovers === DiscountCoversType.ALL_CHARGES) {
        this.inputForm.patchValue({
          whatDiscountCovers: 2
        });
      }
      if (this.discount.applicationOfDiscountWithPercentage === true) {
        this.inputForm.patchValue({
          discountApplication: 1
        });
      } else if (this.discount.applicationOfDiscountWithPercentage === false) {
        this.inputForm.patchValue({
          discountApplication: 2
        });
      }
      if (this.discount.discountOnMoreRaces === true) {
        this.inputForm.patchValue({
          discountOnMoreRaces: 1
        });
      } else if (this.discount.discountOnMoreRaces === false) {
        this.inputForm.patchValue({
          discountOnMoreRaces: 2
        });
      }
      if (this.discount.usageLimit === true) {
        this.inputForm.patchValue({
          usageLimit: 1
        });
      } else if (this.discount.usageLimit === false) {
        this.inputForm.patchValue({
          usageLimit: 2
        });
      }
      if (this.discount.standaloneUsageOfDiscount === true) {
        this.inputForm.patchValue({
          standaloneUsageOfDiscount: 1
        });
      } else if (this.discount.standaloneUsageOfDiscount === false) {
        this.inputForm.patchValue({
          standaloneUsageOfDiscount: 2
        });
      }
      this.startDate = this.discount.dateFrom;
      this.endDate = this.discount.dateTo;
      this.inputForm.updateValueAndValidity();

      this.subscriptions.push(this.inputForm.controls.discountType.valueChanges.subscribe((value: DiscountType) => {
        this.inputForm.controls.discountCode.clearValidators();
        if (value === DiscountType.COUPON) {
          this.inputForm.controls.discountCode.enable();
          this.inputForm.controls.discountCode.addValidators([required]);
          this.inputForm.controls.discountOnMoreRaces.setValue(undefined);
          this.inputForm.controls.discountOnMoreRaces.disable();
          this.inputForm.controls.usageLimit.enable();
          this.inputForm.controls.usageMaxAllowed.enable();
          this.inputForm.patchValue({
            discountOnMoreRaces: 2
          });
          this.inputForm.controls.discountOnMoreRaces.disable();
          this.inputForm.controls.numberOfRaces.setValue(undefined);
          this.inputForm.controls.numberOfRaces.disable();
        } else {
          this.inputForm.controls.discountCode.setValue(undefined);
          this.inputForm.controls.discountCode.disable();
          this.inputForm.controls.discountOnMoreRaces.enable();
          this.inputForm.controls.usageLimit.setValue(undefined);
          this.inputForm.controls.usageLimit.disable();
          this.inputForm.controls.usageMaxAllowed.setValue(undefined);
          this.inputForm.controls.usageMaxAllowed.disable();
          this.inputForm.controls.discountOnMoreRaces.enable();
          this.inputForm.controls.numberOfRaces.enable();
        }
        this.inputForm.controls.discountCode.updateValueAndValidity();
        this.inputForm.markAsTouched();
      }));
    }
  }

  collapseUserWhoHaveUsedTheCoupon() {
    this.isUserWhoHaveUsedTheCouponCollapsed = !this.isUserWhoHaveUsedTheCouponCollapsed;
  }

  collapseUserWhoHaveNotUsedTheCoupon() {
    this.isUserWhoHaveNotUsedTheCouponCollapsed = !this.isUserWhoHaveNotUsedTheCouponCollapsed;
  }

  private discountDataFields(): Discount {
    this.checkValidators(this.inputForm);
    let discount: Discount = {...this.discount}

    discount.discountType = this.inputForm.value.discountType;
    discount.discountCode = this.inputForm.value.discountCode;
    discount.name = this.inputForm.value.name;
    discount.description = this.inputForm.value.description;
    discount.dateFrom = this.inputForm.value.periodValidation?.from;
    discount.dateTo = this.inputForm.value.periodValidation?.to;
    discount.discountCovers = this.inputForm.value.whatDiscountCovers;
    if (this.inputForm.value.discountApplication === 1) {
      discount.applicationOfDiscountWithPercentage = true;
    } else if (this.inputForm.value.discountApplication === 2) {
      discount.applicationOfDiscountWithPercentage = false;
    } else {
      discount.applicationOfDiscountWithPercentage = undefined;
    }
    discount.amount = this.inputForm.value.amountOrPercentage;
    if (this.inputForm.value.discountOnMoreRaces === 1) {
      discount.discountOnMoreRaces = true;
    } else if (this.inputForm.value.discountOnMoreRaces === 2) {
      discount.discountOnMoreRaces = false;
    } else {
      discount.discountOnMoreRaces = undefined;
    }
    discount.active = this.inputForm.value.active;
    if (this.inputForm.value.standaloneUsageOfDiscount === 1) {
      discount.standaloneUsageOfDiscount = true;
    } else if (this.inputForm.value.standaloneUsageOfDiscount === 2) {
      discount.standaloneUsageOfDiscount = false;
    } else {
      discount.standaloneUsageOfDiscount = undefined;
    }
    if (this.inputForm.value.usageLimit === 1) {
      discount.usageLimit = true;
    } else if (this.inputForm.value.usageLimit === 2) {
      discount.usageLimit = false;
    } else {
      discount.usageLimit = undefined;
    }
    discount.usageMaxAllowed = this.inputForm.value.usageMaxAllowed;
    return discount;
  }

  onSave() {
    if (!this.inputForm.valid) {
      this.markAllAsTouched(this.inputForm)
    } else {
      let discount: Discount = this.discountDataFields();
      if (discount !== undefined) {
        discount.organiserId = this.organiserId;
        this.discountsService.updateDiscount(discount, (discount: Discount) => {
          this.discount = discount;
          this.toastService.showToast(TOAST_STATE.success,
            this.translocoService.translate("administrator.discounts.management.save.discounts.success"))
        }, () => {
          this.toastService.showToast(TOAST_STATE.danger,
            this.translocoService.translate("app.common.server.error"))
        }, this.organiserId);
      }
    }
  }

  removeCouponFromUser(user: UserDetail) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("administrator.discounts.management.remove.coupon.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("administrator.discounts.management.remove.coupon.dialog.text");

    modalRef.result.then((_) => {
      this.onRemoveCouponFromUser(user);
    }, (_) => {
      /// ignore
    });
  }

  onRemoveCouponFromUser(user: UserDetail): void {
    this.discountsService.couponUserRequest(new CouponUserRequest(false, this.discount!.id!, user.userId!, this.organiserId)).subscribe((result: boolean) => {
      if (result) {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("administrator.discounts.management.remove.coupon.success"))
        this.subscriptions.push(this.discountsService.findUsersWithCoupon(this.discount!.id!, true, this.organiserId).subscribe(
          (value: UserDetail[]) => {
            this.userWithUnusedCoupons = value ?? [];
          }
        ));
      } else {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.server.error"))
      }
    });
  }

  onSendCouponToUser(user: UserDetail): void {
    this.discountsService.couponUserRequest(new CouponUserRequest(true, this.discount!.id!, user.userId!, this.organiserId)).subscribe((result: boolean) => {
      if (result) {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("administrator.discounts.management.send.coupon.success"))
        this.subscriptions.push(this.discountsService.findUsersWithCoupon(this.discount!.id!, true, this.organiserId).subscribe(
          (value: UserDetail[]) => {
            this.userWithUnusedCoupons = value ?? [];
          }
        ));
      } else {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.server.error"))
      }
    });
  }

  openUsersList() {
    this.discountsService.findUsers(this.discount!, this.organiserId).subscribe((value: UserDetail[]) => {
      if (value && value.length > 0) {
        const modalRef = this.modalService.open(UserListModalComponent, {
          centered: true,
          windowClass: 'usr-list-modal'
        });
        modalRef.componentInstance.title = this.translocoService.translate("administrator.discounts.management.send.coupon.dialog.title");
        modalRef.componentInstance.users = value;
        modalRef.componentInstance.filteredUsers = value;

        modalRef.result.then((user: UserDetail) => {
          this.onSendCouponToUser(user);
        }, (_) => {
          /// ignore
        });
      }
    });
  }

  onRemoveDiscountItem(index: number) {
    if (this.discount && this.discount.discountOnMoreRaces && this.discount.discountOnMoreRacesItems && this.discount.discountOnMoreRacesItems.length > 0) {
      this.discount.discountOnMoreRacesItems.splice(index, 1);
      let discountOnMoreRacesItems: DiscountItem[] = this.discount.discountOnMoreRacesItems;
      let tags: string[] = discountOnMoreRacesItems.map((value: DiscountItem) => {
        return this.constructDiscountItemUILabel(value);
      });
      this.inputForm.controls.numberOfRaces.setValue(tags);
    }
  }

  private constructDiscountItemUILabel(value: DiscountItem): string {
    let discountApplication = this.inputForm.value.discountApplication;
    let discountApplicationStr: string = "";
    if (discountApplication === 1) {
      discountApplicationStr = "%"
    } else if (discountApplication === 2) {
      discountApplicationStr = "";
    }
    return this.translocoService.translate("administrator.discounts.management.discountItem.template", {
      numberOfRaces: value.numberOfRaces,
      amount: value.amount,
      amountType: discountApplicationStr
    });
  }

  addDiscountItem() {
    const modalRef = this.modalService.open(AddDiscountItemModalComponent, {
      centered: true,
      windowClass: 'add-discount-item-modal'
    });

    modalRef.result.then((discountItem: DiscountItem) => {
      if (this.discount!.discountOnMoreRacesItems === undefined) {
        this.discount!.discountOnMoreRacesItems
      }
      this.discount?.discountOnMoreRacesItems?.push(discountItem);
      if (this.discount!.discountOnMoreRaces && this.discount!.discountOnMoreRacesItems && this.discount!.discountOnMoreRacesItems.length > 0) {
        let discountOnMoreRacesItems: DiscountItem[] = this.discount!.discountOnMoreRacesItems;
        let tags: string[] = discountOnMoreRacesItems.map((value: DiscountItem) => {
          return this.constructDiscountItemUILabel(value);
        });
        this.inputForm.controls.numberOfRaces.setValue(tags);
      }
    }, (_) => {
      /// ignore
    });
  }

  isDiscountOnMoreRacesItemsFieldDisabled(): boolean {
    return (this.inputForm.value.discountType === 1);
  }

  openEventsForDiscountList() {
    this.discountsService.findEventsForDiscount(this.organiserId).subscribe((eventItems: EventItem[]) => {
      if (eventItems && eventItems.length > 0) {
        for (var event of eventItems) {
          if (this.discount?.events && this.discount.events.length > 0) {
            for (var eventItem of this.discount.events) {
              if (event.id === eventItem.id) {
                event.discountApplied = true;
                if (event && event.racesList && event.racesList.length > 0) {
                  for (var race of event.racesList) {
                    if (eventItem && eventItem.racesList && eventItem.racesList.length > 0) {
                      for (var raceItem of eventItem.racesList) {
                        if (race.id === raceItem.id) {
                          race.discountApplied = raceItem.discountApplied;
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        const modalRef: NgbModalRef = this.modalService.open(EventsListModalComponent, {
          centered: true,
          windowClass: 'discount-list-modal'
        });
        modalRef.componentInstance.events = eventItems;
        modalRef.componentInstance.title = "administrator.discounts.management.eventsRacesDiscountIsAppliedTo"
        modalRef.componentInstance.tableColumnName = "administrator.discounts.management.eventsRacesDiscountIsAppliedTo.event.name"

        modalRef.result.then((events: EventItem[]) => {
          if (this.discount) {
            this.discount.events = events;

            let eventsRacesDiscountIsAppliedToItems: EventItem[] = this.discount.events;
            // @ts-ignore
            let tags: string[] = eventsRacesDiscountIsAppliedToItems.map((value: EventItem) => {
              return getLanguageValue(value.name, value.nameEng, value.nameIta, value.nameSlo, value.nameSr, value.nameBs, "hr") ?? ""
            });
            this.inputForm.controls.eventsRacesDiscountIsAppliedTo.setValue(tags);
          }
        }, (_) => {
          /// ignore
        });
      }
    });
  }

  onRemoveEventDiscountItem(index: number) {
    if (this.discount && this.discount.events && this.discount.events.length > 0) {
      this.discount.events.splice(index, 1);
      let eventsRacesDiscountIsAppliedToItems: EventItem[] = this.discount.events;
      // @ts-ignore
      let tags: string[] = eventsRacesDiscountIsAppliedToItems.map((value: EventItem) => {
        return getLanguageValue(value.name, value.nameEng, value.nameIta, value.nameSlo, value.nameSr, value.nameBs, "hr") ?? ""
      });
      this.inputForm.controls.eventsRacesDiscountIsAppliedTo.setValue(tags);
    }
  }
}
